import React, { useMemo, useState } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import SearchIcon from '@material-ui/icons/Search'
import Divider from '@material-ui/core/Divider'
import ExpansionPanel from '@material-ui/core/ExpansionPanel'
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary'
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import LiquidFormLabel from '../LiquidFormLabel'
import LiquidFormHelpText from '../LiquidFormHelpText'
import { useIntl } from 'react-intl'
import { Field, Form, Formik } from 'formik'
import { format } from 'date-fns-next'
import messages from '@utils/messages'
import { useStockAvailabilityLazyQuery } from '@src/queries/StockAvailabilityQuery.generated'
import { StockItem } from '@src/types/graphql-types'
import LiquidInputAdapter from '../LiquidInputAdapter'
import { Theme } from '@material-ui/core/'
import { makeStyles } from '@material-ui/styles'
import { useTheme } from '@material-ui/core/styles'

interface Props {
  setInventorySearch: React.Dispatch<React.SetStateAction<boolean>>
}

interface ResultsProps {
  items: Maybe<StockItem>[]
  selectedBrand: string | null
  setSelectedBrand: React.Dispatch<React.SetStateAction<string | null>>
}

const useStyles = makeStyles((theme: Theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.secondary.light,
  },
  inventorySearchButton: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
    padding: theme.typography.pxToRem(8),
    '&:hover': { backgroundColor: theme.palette.primary.dark },
  },
  inventorySearchIcon: {
    color: theme.palette.common.white,
  },
  inventoryBrandButton: {
    justifyContent: 'flex-start',
    fontSize: theme.typography.pxToRem(19),
    fontWeight: theme.typography.fontWeightRegular,
  },
  expansionPanelSummary: {
    flexDirection: 'row-reverse',
    padding: 0,
  },
  expansionPanelSummaryIcon: {
    '&.expanded': {
      transform: 'rotate(90deg)',
    },
  },
  inventorySearchProductSDS: {
    display: 'flex',
    justifyContent: 'center',
  },
  inventorySearchField: {
    borderColor: '#e6e6ea',
    backgroundColor: '#e6e6ea',
  },
}))

const InventorySearchResults: React.FC<ResultsProps> = ({
  items,
  selectedBrand,
  setSelectedBrand,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()

  if (items.length === 0) {
    return null
  }

  if (!selectedBrand && items.length > 1) {
    return (
      <Grid container spacing={4} style={{ marginTop: '1rem' }}>
        <Grid item xs={12}>
          <Typography style={{ fontSize: '1.1rem' }}>
            {`${formatMessage(messages.SELECT_PRODUCT)}:`}
          </Typography>
        </Grid>
        {items.map((item) => (
          <Grid key={item?.brand} item xs={12}>
            <Button
              className={classes.inventoryBrandButton}
              fullWidth
              size="large"
              variant="outlined"
              onClick={() => setSelectedBrand(item?.brand ?? null)}
            >
              {`${item?.product} ${item?.brand} `}
              <span>
                <strong>{item?.description ?? ''}</strong>
              </span>
            </Button>
          </Grid>
        ))}
      </Grid>
    )
  }

  const item = selectedBrand
    ? items.find((item) => item?.brand === selectedBrand)
    : items[0]

  return (
    <>
      <Grid container style={{ padding: '1rem' }}>
        <Grid item xs={2}>
          <Typography color="primary">{item?.product}</Typography>
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={6}></Grid>
        <Grid className={classes.inventorySearchProductSDS} item xs={1}>
          <Typography>{formatMessage(messages.SDS)}</Typography>
        </Grid>
        {/* Functionality for this icon will be added at a later time */}
        <Grid className={classes.inventorySearchProductSDS} item xs={1}>
          <ExpandMoreIcon color="secondary" />
        </Grid>
      </Grid>
      {item?.materials.map((material) => (
        <ExpansionPanel key={material.materialNumber}>
          <ExpansionPanelSummary
            className={classes.expansionPanelSummary}
            classes={{
              expandIcon: classes.expansionPanelSummaryIcon,
              expanded: 'expanded',
            }}
            expandIcon={<ChevronRightIcon fontSize="large" color="secondary" />}
          >
            <Typography>
              <span>{material.materialNumber}</span> {material.description}
            </Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{formatMessage(messages.BATCH_LOT)}</TableCell>
                    <TableCell>{formatMessage(messages.UNITS)}</TableCell>
                    <TableCell>{formatMessage(messages.WAREHOUSE)}</TableCell>
                    <TableCell>{formatMessage(messages.EXPIRATION)}</TableCell>
                    <TableCell>
                      {formatMessage(messages.GLOBAL_ITEM_STATUS)}
                    </TableCell>
                    <TableCell>
                      {formatMessage({
                        id: 'DOCUMENTS',
                        defaultMessage: 'Documents',
                      })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {material.inventoryDetails.map((detail, index) => (
                    <TableRow key={`${detail.plant}-${detail.batch}-${index}`}>
                      <TableCell>{detail.batch}</TableCell>
                      <TableCell>{detail.availableQuantity}</TableCell>
                      <TableCell>{detail.plant}</TableCell>
                      <TableCell>
                        {format(detail.expirationDate, 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>{detail.glbInvDesc}</TableCell>
                      <TableCell>
                        {/* TODO: Will get added in STRAT-298 */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      ))}
    </>
  )
}

const InventorySearchModal: React.FC<Props> = ({ setInventorySearch }) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null)
  const [getStockAvailability, { data, loading }] =
    useStockAvailabilityLazyQuery()
  const inventoryItems = data?.getStockAvailability.items ?? []
  const rawErrorMessage = data?.getStockAvailability.errorMessage
  const theme = useTheme()

  let errorMessage = useMemo(() => {
    if (rawErrorMessage?.startsWith('Invalid Material')) {
      return formatMessage(messages.INVALID_PRODUCT_NUMBER)
    }
    if (rawErrorMessage === 'No record found') {
      return formatMessage(messages.NO_RECORD_FOUND)
    }
    return ''
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawErrorMessage])

  const handleSubmit = async ({ product }) => {
    if (!product) {
      errorMessage = formatMessage(messages.PRODUCT_NUMBER_REQUIRED)
    } else {
      setSelectedBrand(null)
      await getStockAvailability({
        variables: { product },
      })
    }
  }

  return (
    <Formik initialValues={{ product: '' }} onSubmit={handleSubmit}>
      {({ setFieldValue }) => (
        <Dialog maxWidth="md" fullWidth open>
          <DialogTitle>
            {formatMessage(messages.SEARCH_INVENTORY_BATCH)}
            <IconButton
              className={classes.closeButton}
              disabled={loading}
              onClick={() => setInventorySearch(false)}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ marginBottom: '2rem' }}>
            <Form>
              <Grid
                container
                spacing={2}
                alignItems="flex-end"
                style={{ marginBottom: '1rem' }}
              >
                <Grid item xs={12}>
                  <LiquidFormLabel
                    style={{ marginBottom: 0 }}
                    htmlFor="product-number"
                  >
                    {formatMessage(messages.PRODUCT_NUMBER)}
                  </LiquidFormLabel>
                </Grid>
                <Grid item xs={11}>
                  <Field
                    className={classes.inventorySearchField}
                    id="product-number"
                    style={
                      errorMessage
                        ? { border: `1px solid ${theme.palette.rich.red}` }
                        : null
                    }
                    name="product"
                    filled
                    component={LiquidInputAdapter}
                    onChange={(e) => {
                      errorMessage = !e.target.value
                        ? formatMessage(messages.PRODUCT_NUMBER_REQUIRED)
                        : ''
                      setFieldValue('product', e.target.value)
                    }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    id="inventory-search-submit"
                    data-testid="inventory-search-submit"
                    className={classes.inventorySearchButton}
                    type="submit"
                    aria-label="search"
                  >
                    <SearchIcon className={classes.inventorySearchIcon} />
                  </IconButton>
                </Grid>
                <Grid item xs={12}>
                  {errorMessage && (
                    <LiquidFormHelpText style={{ margin: 0 }} error>
                      {errorMessage}
                    </LiquidFormHelpText>
                  )}
                </Grid>
              </Grid>
            </Form>
            {loading ? (
              <>
                <Divider style={{ margin: '2rem 0' }} />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </div>
              </>
            ) : (
              inventoryItems?.length > 0 && (
                <>
                  <Divider />
                  <InventorySearchResults
                    items={inventoryItems}
                    selectedBrand={selectedBrand}
                    setSelectedBrand={setSelectedBrand}
                  />
                </>
              )
            )}
          </DialogContent>
        </Dialog>
      )}
    </Formik>
  )
}

export default InventorySearchModal
